// Student view session: an instructor opens a separate TAB that interacts with
// a course as their per-course test student account. The mode is carried by
// URL parameters on the opened tab's first load, then persisted in
// sessionStorage — which is per-tab, so the instructor's other tabs (and their
// Firebase login, which is shared browser-wide) are never affected.

const STORAGE_KEY = 'kudu-student-view';

// Server-derived reserved namespace (see backend student-view-api.go). The id
// embeds the course and the authorizing instructor.
export const STUDENT_VIEW_USER_PREFIX = 'student-view|';

export interface StudentViewSession {
  courseId: string;
  userId: string;
  // Reveal content that visibility settings hide from students (display-only;
  // never written back to any setting). Defaults to off — the accurate view.
  showHiddenContent?: boolean;
}

// isStudentViewUserId accepts only ids the backend could have derived FOR THIS
// course: reserved prefix and a matching embedded course id. This prevents a
// crafted link from making the tab act as an arbitrary account.
export function isStudentViewUserId(userId: string, courseId: string): boolean {
  if (!userId.startsWith(STUDENT_VIEW_USER_PREFIX)) {
    return false;
  }

  const rest = userId.slice(STUDENT_VIEW_USER_PREFIX.length);
  const separator = rest.indexOf('|');

  if (separator <= 0 || separator === rest.length - 1) {
    return false;
  }

  return rest.slice(0, separator) === courseId;
}

function parseSession(raw: unknown): StudentViewSession | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }

  const { courseId, showHiddenContent, userId } = raw as Record<string, unknown>;

  if (typeof courseId !== 'string' || typeof userId !== 'string') {
    return null;
  }

  if (!courseId || !isStudentViewUserId(userId, courseId)) {
    return null;
  }

  return {
    courseId,
    userId,
    showHiddenContent: showHiddenContent === true,
  };
}

/**
 * Persist the show-hidden-content preference onto this tab's session so it
 * survives reloads and in-tab navigation. No-op when no session is active.
 */
export function persistShowHiddenContent(value: boolean): void {
  const session = readStudentViewSession();

  if (!session) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...session, showHiddenContent: value }),
    );
  } catch {
    // Session storage unavailable — the toggle just won't survive a reload.
  }
}

export function readStudentViewSession(): StudentViewSession | null {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);

    if (!stored) {
      return null;
    }

    return parseSession(JSON.parse(stored));
  } catch {
    return null;
  }
}

export function clearStudentViewSession(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Session storage unavailable — nothing to clear.
  }
}

/**
 * Resolve the tab's student-view session: URL parameters (set by the opener)
 * take precedence and are persisted to sessionStorage, then stripped from the
 * address bar so reloads and in-tab navigation stay in the mode via storage.
 */
export function initializeStudentViewSession(): StudentViewSession | null {
  try {
    const url = new URL(window.location.href);
    const courseId = url.searchParams.get('studentView');
    const userId = url.searchParams.get('studentViewUser');

    if (courseId && userId) {
      const session = parseSession({ courseId, userId });

      if (session) {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      }

      url.searchParams.delete('studentView');
      url.searchParams.delete('studentViewUser');
      window.history.replaceState(window.history.state, '', url.toString());

      if (session) {
        return session;
      }
    }
  } catch {
    // Fall through to the stored session.
  }

  return readStudentViewSession();
}

/** URL for the opener: the target page plus the student-view parameters. */
export function buildStudentViewUrl(
  path: string,
  session: StudentViewSession,
): string {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}studentView=${encodeURIComponent(session.courseId)}&studentViewUser=${encodeURIComponent(session.userId)}`;
}
